<template>
  <template v-if="isAtriumTenderKindDropdown">
    <FieldValidator
      :name="fieldName"
      v-slot="{ value, handleBlur, validate, isInvalid, errorMessage }"
    >
      <!-- @vue-expect-error FIXME: Argument of type 'FieldValidator' is not assignable to parameter type -->
      <Dropdown
        :inputId="`tender-type-${planType}-${index}`"
        :modelValue="atriumDropdownValue(value)"
        :options="tenderTypes"
        optionLabel="label"
        optionValue="value"
        :placeholder="translate(`${LOCALE_PREFIX}.form.tenders.tenderTypePlaceholder`)"
        :required="!isDisabled"
        :disabled="isDisabled"
        :class="{ 'p-invalid': isInvalid }"
        @update:modelValue="(v) => onAtriumTenderKindChange(v, validate)"
        @blur="handleBlur"
        @hide="validate"
      >
        <template #label>{{ translate(`${LOCALE_PREFIX}.form.tenders.tenderType`) }}</template>
        <template v-if="isInvalid" #helperText>{{ errorMessage }}</template>
      </Dropdown>
    </FieldValidator>
  </template>
  <DropdownField
    v-else
    :inputId="`tender-type-${planType}-${index}`"
    :name="fieldName"
    :label="translate(`${LOCALE_PREFIX}.form.tenders.tenderType`)"
    :placeholder="translate(`${LOCALE_PREFIX}.form.tenders.tenderTypePlaceholder`)"
    :options="tenderTypes"
    optionLabel="label"
    optionValue="value"
    :required="!isDisabled"
    :disabled="isDisabled"
    @change="addTenderRates($event?.value)"
  />
</template>

<script setup lang="ts">
import { useTranslation } from '@centric-os/helpers';
import { SitesLocalePrefix } from '../../../../../enums';
import { useFieldArray, useFieldValue } from 'vee-validate';
import { computed, inject, nextTick } from 'vue';
import {
  ATRIUM_TYPES,
  ITC_TYPES,
  TENDER_TYPES,
  FREEDOM_PAY_TYPES,
  DECLINING_BALANCE_WITH_SPLIT,
} from '../../enums/enums';
import { type Tender, type MeqTender, type DbTender, TenderTypes } from '@centric-os/types';
import { cloneDeep } from 'lodash-es';
import { useMealPlan } from '../../meal-plan/composables';
import { useCashlessPay } from '../../cashless-pay/composables';
import { DropdownField } from '@centric-os/components/src/composed/validators/dropdown-field';
import { FieldValidator } from '@centric-os/components/src/composed/validators/field-validator';
import { Dropdown } from '@centric-os/components/src/core/controls/dropdown';

// Define props
const props = defineProps({
  fieldName: {
    type: String,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  planType: {
    type: String,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
});

// Set localization
const { translate } = useTranslation();
const LOCALE_PREFIX = SitesLocalePrefix.SITE_MEAL_PLAN;

const formNamespace = inject('formNamespace', '');

// Set plan type field value
const planType = useFieldValue<string>(`${formNamespace}type`);

// Set tenders field array
const { update, fields } = useFieldArray<Tender>(`${formNamespace}tenders`);

// Set meal plan composable
const { tenderTypes: mealPlanTenders } = useMealPlan();

// Set cashless pay tender types
const { tenderTypes: cashlessPayTenders } = useCashlessPay();

const isAtriumTenderKindDropdown = computed(
  () => planType.value != null && ATRIUM_TYPES.includes(planType.value),
);

const currentTender = computed(() => fields.value[props.index]?.value as Tender | undefined);

// Set tender types based on meal plan type
const tenderTypes = computed<{ label: string; value: string }[]>(() => {
  if (ITC_TYPES.includes(planType.value)) {
    return mealPlanTenders.value?.filter((type) => type.value === TENDER_TYPES.DECLINING_BALANCE);
  }

  if (ATRIUM_TYPES.includes(planType.value)) {
    return mealPlanTenders.value?.filter((type) => type.value !== TENDER_TYPES.MEAL_EXCHANGE);
  }

  // Check if cashless pay & freedom pay tender types
  if (FREEDOM_PAY_TYPES.includes(planType.value)) {
    return cashlessPayTenders.value;
  }

  return mealPlanTenders.value?.filter((type) => type.value !== DECLINING_BALANCE_WITH_SPLIT);
});

const atriumDropdownValue = (typeValue: unknown): string => {
  const v = typeValue as string;
  const t = currentTender.value;
  if (
    v === TenderTypes.DECLINING_BALANCE &&
    t &&
    t.type === TenderTypes.DECLINING_BALANCE &&
    (t as DbTender).stipend_split === true
  ) {
    return DECLINING_BALANCE_WITH_SPLIT;
  }
  return v;
};

const onAtriumTenderKindChange = (v: string, validate: () => Promise<boolean> | boolean | void) => {
  const prev = currentTender.value;
  if (prev == null) {
    return;
  }

  if (v === TenderTypes.MEAL_EQUIVALENT) {
    const tender = cloneDeep(prev) as MeqTender;
    tender.type = TenderTypes.MEAL_EQUIVALENT;
    tender.rates = [
      {
        amount: 0,
      },
    ];
    // @ts-expect-error FIXME: Conversion of type 'MeqTender' to type 'Partial<DbTender>' may be a mistake b...
    delete (tender as Partial<DbTender>).stipend_split;
    update(props.index, tender);
    void nextTick(() => validate());
    return;
  }

  if (v === DECLINING_BALANCE_WITH_SPLIT) {
    const tender = cloneDeep(prev) as DbTender;
    tender.type = TenderTypes.DECLINING_BALANCE;
    tender.stipend_split = true;
    // @ts-expect-error FIXME: Conversion of type 'DbTender' to type 'Partial<MeqTender>' may be a mistake b...
    delete (tender as Partial<MeqTender>).rates;
    // @ts-expect-error FIXME: Conversion of type 'DbTender' to type 'Partial<MeqTender>' may be a mistake b...
    delete (tender as Partial<MeqTender>).max_swipes_per_transaction;
    update(props.index, tender);
    void nextTick(() => validate());
    return;
  }

  if (v === TENDER_TYPES.DECLINING_BALANCE) {
    const tender = cloneDeep(prev) as DbTender;
    tender.type = TenderTypes.DECLINING_BALANCE;
    delete (tender as Partial<DbTender>).stipend_split;
    // @ts-expect-error FIXME: Conversion of type 'DbTender' to type 'Partial<MeqTender>' may be a mistake b...
    delete (tender as Partial<MeqTender>).rates;
    // @ts-expect-error FIXME: Conversion of type 'DbTender' to type 'Partial<MeqTender>' may be a mistake b...
    delete (tender as Partial<MeqTender>).max_swipes_per_transaction;
    update(props.index, tender);
    void nextTick(() => validate());
    return;
  }
};

// Method to add tender rates if is of type meal equivalent
const addTenderRates = (value: TenderTypes): void => {
  if (value === TenderTypes.MEAL_EQUIVALENT) {
    const tender = cloneDeep(fields.value[props.index]?.value) as MeqTender;
    tender.type = value;
    tender.rates = [
      {
        amount: 0,
      },
    ];

    update(props.index, tender);
  }
};
</script>
